import { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";

export function ShowingRequestDialog({ open, onClose, slug, agentName, address }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", message: "" });
  const [state, setState] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setState("submitting");
    setError("");
    try {
      await api.post(`/public/listing/${slug}/showing-request`, form);
      setState("success");
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong — please try again.");
      setState("idle");
    }
  };

  const close = () => { onClose(); setTimeout(() => { setState("idle"); setForm({ name: "", email: "", phone: "", preferred_date: "", preferred_time: "", message: "" }); }, 200); };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent data-testid="listing-showing-dialog" className="bg-white border border-[#E7E4DE] rounded-2xl max-w-md">
        {state === "success" ? (
          <div className="py-6 text-center" data-testid="listing-showing-success">
            <div className="mx-auto h-12 w-12 rounded-full bg-[#C7A361]/15 grid place-items-center"><Check size={22} className="text-[#C7A361]" /></div>
            <h3 className="mt-4 font-serif-display text-xl text-[#202833]">Request sent</h3>
            <p className="mt-2 text-sm text-[#667085]">{agentName ? `${agentName.split(" ")[0]} will` : "The listing agent will"} be in touch to confirm a time.</p>
            <Button data-testid="listing-showing-done" onClick={close} className="mt-6 rounded-full bg-[#C7A361] text-[#111820] hover:brightness-95">Done</Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="font-serif-display text-2xl font-normal text-[#202833]">Schedule a Showing</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-[#667085] -mt-2">{address}</p>
            <form onSubmit={submit} className="space-y-3.5 mt-1">
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#667085]">Your name</Label>
                <Input data-testid="listing-showing-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#667085]">Email</Label>
                  <Input data-testid="listing-showing-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#667085]">Phone</Label>
                  <Input data-testid="listing-showing-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#667085]">Preferred date</Label>
                  <Input data-testid="listing-showing-date" type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#667085]">Preferred time</Label>
                  <Input data-testid="listing-showing-time" type="time" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-[0.12em] text-[#667085]">Message (optional)</Label>
                <Textarea data-testid="listing-showing-message" rows={2} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-lg border-[#E7E4DE]" />
              </div>
              {error && <p data-testid="listing-showing-error" className="text-xs text-red-600">{error}</p>}
              <p className="text-[11px] text-[#667085] leading-relaxed">This is a request only — your preferred time isn't guaranteed until the agent confirms availability.</p>
              <Button data-testid="listing-showing-submit" type="submit" disabled={state === "submitting"} className="w-full rounded-full bg-[#C7A361] text-[#111820] hover:brightness-95 font-semibold">{state === "submitting" ? "Sending…" : "Request Showing"}</Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
